import { createElement, useContext, useMemo, useRef, useState } from "react";
import { useMaterialReactTable, type MRT_ColumnDef, MRT_Row } from "material-react-table";

import { UserContext } from "../../../hooks/UserContext";
import { useCompanyContext } from "../../../hooks/CompanyContext";
import type { Job } from "../../../models";
import { LoadingComponent } from "../../helpers";

import type { Props, JobsDashboardTableViewModel } from "./JobsDashboardTable.types";
import { booleanFilterFn, getMarkReadyText } from "./JobsDashboardTable.helpers";
import { buildColumns, buildDisplayColumnDefOptions } from "./JobsDashboardTable.columns";
import { useDashboardState } from "./hooks/JobsDashboardState";
import { useFileDownloads } from "./hooks/JobsDashboardFileDownloads";
import { useCompanyViewsAndJobs } from "./hooks/JobsDashboardCompanyViewsAndJobs";

export function useJobsDashboardTableViewModel(_props: Props): JobsDashboardTableViewModel {
	const userContext = useContext(UserContext);
	const { selectedCompany, companies, isInitialized: companyContextInitialized } = useCompanyContext();

	// #region DASHBOARD STATE (extracted hook)
	const ds = useDashboardState();
	const {
		sorting,
		setSorting,
		grouping,
		setGrouping,
		columnOrder,
		setColumnOrder,
		columnVisibility,
		setColumnVisibility,
		columnFilters,
		setColumnFilters,
		columnFilterFns,
		setColumnFilterFns,
		hasGrouping,
	} = ds;
	// #endregion

	// #region COMPANY, VIEWS, JOBS (extracted hook)
	const data = useCompanyViewsAndJobs({
		dashboardState: ds,
		userContextId: userContext?.id,
		userEmail: userContext?.email,
		selectedCompany,
		companyOptions: companies,
		authUser: userContext,
		companyContextInitialized,
	});
	// #endregion

	// #region UI STATE (owned by main VM)
	const [markJobsReadyVisible, setMarkJobsReadyVisible] = useState(false);
	const [mapModalOpen, setMapModalOpen] = useState(false);
	const [acresAggregation, setAcresAggregation] = useState<"total" | "average">("total");
	const [actionMenuElement, setActionMenuElement] = useState<null | HTMLElement>(null);
	const [actionMenuRow, setActionMenuRow] = useState<MRT_Row<Job> | null>(null);
	// #endregion

	// #region TABLE CONFIG
	const columns = useMemo<MRT_ColumnDef<Job>[]>(
		() =>
			buildColumns({
				acresAggregation,
				setAcresAggregation,
				getBranchField: data.getBranchField,
				getRegionField: data.getRegionField,
				jobStatusList: data.jobStatusList,
				testPackageList: data.testPackageList,
				submitterList: data.submitterList,
				seasonList: data.seasonList,
				branchList: data.branchList,
				regionList: data.regionList,
			}),
		[
			data.branches,
			data.jobStatusList,
			data.testPackageList,
			data.submitterList,
			data.seasonList,
			data.branchList,
			data.regionList,
			data.regions,
			acresAggregation,
		],
	);

	const tableHeight = data.hasSelections && hasGrouping ? `calc(100vh - 240px)` : `calc(100vh - 190px)`;

	const table = useMaterialReactTable({
		columns,
		data: data.jobs,
		enableColumnFilterModes: true,
		enableColumnFilters: true,
		enableColumnOrdering: true,
		enableColumnDragging: true,
		enableColumnPinning: true,
		enableGrouping: true,
		enableHiding: true,
		enableFacetedValues: true,
		enableStickyHeader: true,
		enableStickyFooter: true,
		enableTableFooter: true,
		enableMultiSort: true,
		enableGlobalFilterModes: true,
		enableGlobalFilterRankedResults: true,
		enableRowSelection: true,
		enableSubRowSelection: true,
		enableRowActions: true,
		positionActionsColumn: "last",
		columnFilterDisplayMode: "subheader",
		muiTableContainerProps: { sx: { height: tableHeight } },
		muiTablePaperProps: { sx: { height: "100vh - 30px)" } },
		maxMultiSortColCount: 3,
		initialState: {
			expanded: true,
			showGlobalFilter: true,
			density: "compact",
			pagination: {
				pageSize: 20,
				pageIndex: 0,
			},
			columnVisibility: {
				Grower: false,
				"Test Job?": false,
			},
			columnPinning: {
				left: ["mrt-row-select"],
				right: ["mrt-row-actions"],
			},
		},
		filterFns: {
			booleanFilter: booleanFilterFn as any,
		},
		selectAllMode: "all",
		state: {
			columnVisibility,
			grouping,
			sorting,
			columnOrder,
			columnFilters: columnFilters,
			columnFilterFns: columnFilterFns,
			rowSelection: data.rowSelection,
			isLoading: data.isLoading,
		},
		muiCircularProgressProps: {
			Component: createElement(LoadingComponent),
		},
		onGroupingChange: setGrouping,
		onRowSelectionChange: data.setRowSelection,
		onColumnVisibilityChange: setColumnVisibility,
		onColumnOrderChange: setColumnOrder,
		onColumnFilterFnsChange: setColumnFilterFns,
		onColumnFiltersChange: setColumnFilters,
		onSortingChange: setSorting,
		groupedColumnMode: false,
		displayColumnDefOptions: buildDisplayColumnDefOptions({
			setActionMenuElement,
			setActionMenuRow,
			grouping,
		}),
		paginationDisplayMode: "default",
		positionToolbarAlertBanner: "bottom",
		muiSearchTextFieldProps: {
			size: "small",
			variant: "outlined",
		},
		muiPaginationProps: {
			rowsPerPageOptions: [10, 15, 20, 30, 50],
			showRowsPerPage: true,
		},
		enableTopToolbar: false,
		localization: {
			noRecordsToDisplay: "No jobs found. Please select a saved view above.",
		},
	});
	// #endregion

	// #region SELECTED JOBS (from table instance, resolves group selections to leaf rows)
	const selectedJobs = useMemo(
		() =>
			table
				.getSelectedRowModel()
				.flatRows.filter((row) => !row.getIsGrouped())
				.map((row) => row.original),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[data.rowSelection],
	);
	const unreadySelectedJobs = useMemo(() => selectedJobs.filter((job) => !job?.dateFieldReady), [selectedJobs]);
	// #endregion

	// #region FILE DOWNLOADS (extracted hook)
	const tableRef = useRef(table);
	tableRef.current = table;

	const fileDownloads = useFileDownloads({
		jobs: data.jobs,
		selectedJobs,
		userEmail: userContext?.email,
		tableRef,
		actionMenuRow,
	});
	// #endregion

	return {
		table,
		jobs: data.jobs,
		user: data.user,
		companyOptions: data.companyOptions,
		selectedCompany: data.selectedCompany,
		views: data.views,
		selectedView: data.selectedView,
		branches: data.branches,
		isLoading: data.isLoading,
		rowSelection: data.rowSelection,
		markJobsReadyVisible,
		setMarkJobsReadyVisible,
		mapModalOpen,
		setMapModalOpen,
		inputDialogState: data.inputDialogState,
		mobileMenuElement: data.mobileMenuElement,
		setMobileMenuElement: data.setMobileMenuElement,
		actionMenuElement,
		setActionMenuElement,
		actionMenuRow,
		setActionMenuRow,
		searchParams: ds.searchParams,
		hasSelections: data.hasSelections,
		hasGrouping,
		selectedJobs,
		unreadySelectedJobs,
		noViewSelected: data.noViewSelected,
		noJobs: data.noJobs,
		saveViewDisabled: data.saveViewDisabled,
		deleteViewDisabled: data.deleteViewDisabled,
		copyLinkDisabled: data.copyLinkDisabled,
		copyLinkTooltip: data.copyLinkTooltip,
		viewIsReadonly: data.viewIsReadonly,
		onCompanyChanged: data.onCompanyChanged,
		onSavedViewChanged: data.onSavedViewChanged,
		refreshJobs: data.refreshJobs,
		exportCSV: fileDownloads.exportCSV,
		deleteView: data.deleteView,
		saveCurrentView: data.saveCurrentView,
		copyLinkToClipboard: data.copyLinkToClipboard,
		downloadFiles: fileDownloads.downloadFiles,
		downloadGeoInFormat: fileDownloads.downloadGeoInFormat,
		downloadMergedGeoInFormat: fileDownloads.downloadMergedGeoInFormat,
		copyJobId: fileDownloads.copyJobId,
		getMarkReadyText: getMarkReadyText,
		getDownloadMenuContext: fileDownloads.getDownloadMenuContext,
	};
}
